import React from "react";
import {
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  FormText,
} from "reactstrap";
import Switch from "react-bootstrap-switch";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

// --- Font Options ---
const fontOptions = [
  // ... (font options remain the same)
  { value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', label: 'System Default' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Helvetica Neue", Helvetica, Arial, sans-serif', label: 'Helvetica Neue' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
];

// --- Helper component ---
const NoSettings = ({ message }) => (
  // ... (NoSettings component remains the same)
 <div
    className="d-flex flex-column align-items-center justify-content-center text-center p-4"
    style={{
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      minHeight: "150px",
    }}
  >
    <FontAwesomeIcon
      icon={faInfoCircle}
      className="text-muted mb-2"
      size="2x"
    />
    <h6 className="text-muted mb-0">{message}</h6>
  </div>
);


// --- Configuration Object for ALL Appearance Settings ---
const appearanceSettingsConfig = {
  // --- Global Theme Settings ---
  dark_mode: {
    label: "Dark Mode",
    type: "switch",
    // Applicable to all templates that use getCss
    templates: ["tournament", "single-match", "full-match-detail", "tournament-points-table", "recent-tournaments-list"],
    default: false,
    isGlobal: true, // Flag to save directly in widget_data
    group: "Global Theme" // Grouping for UI
  },
  bg_color: {
    label: "Primary Background",
    type: "hex_color",
    templates: ["tournament", "single-match", "full-match-detail", "tournament-points-table", "recent-tournaments-list"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: true,
    group: "Global Theme"
  },
  text_color: {
    label: "Primary Text",
    type: "hex_color",
    templates: ["tournament", "single-match", "full-match-detail", "tournament-points-table", "recent-tournaments-list"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: true,
    group: "Global Theme"
  },

  // --- Common Template Settings ---
  fontFamily: {
    label: "Font Family",
    type: "select",
    options: fontOptions,
    templates: ["tournament", "single-match", "full-match-detail", "tournament-points-table", "recent-tournaments-list"],
    default: fontOptions[0].value,
    isGlobal: false, // Belongs in template_data
    group: "General" // Grouping for UI
  },

  // --- Tournament / Single Match Settings ---
  showVenue: {
    label: "Show Venue/Location",
    type: "switch",
    templates: ["tournament", "single-match"],
    default: true,
    isGlobal: false,
    group: "Card Options"
  },
  showShortNames: {
    label: "Show Team Short Names",
    type: "switch",
    templates: ["tournament", "single-match"],
    default: true,
    isGlobal: false,
    group: "Card Options"
  },
  cardTextColor: {
    label: "Card Text",
    type: "hex_color",
    templates: ["tournament", "single-match"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Card Colors"
  },
  cardBackgroundColor: {
    label: "Card Background",
    type: "hex_color",
    templates: ["tournament", "single-match"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Card Colors"
  },
  liveColor: {
    label: '"Live" Status',
    type: "hex_color",
    templates: ["tournament", "single-match"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Card Colors"
  },

  // --- Full Match Detail Settings ---
  showStats: {
    label: "Show Match Statistics",
    type: "switch",
    templates: ["full-match-detail"],
    default: true,
    isGlobal: false,
    group: "Content Options"
  },
  showEvents: {
    label: "Show Match Events",
    type: "switch",
    templates: ["full-match-detail"],
    default: true,
    isGlobal: false,
    group: "Content Options"
  },
  homeTeamColor: {
    label: "Home Team",
    type: "hex_color",
    templates: ["full-match-detail"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Team Colors"
  },
  awayTeamColor: {
    label: "Away Team",
    type: "hex_color",
    templates: ["full-match-detail"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Team Colors"
  },

  // --- Points Table Settings ---
  highlightTopTeams: {
    label: "Highlight Top 3 Teams",
    type: "switch",
    templates: ["tournament-points-table"],
    default: false,
    isGlobal: false,
    group: "Table Options"
  },
  pointsTableHeaderBg: {
    label: "Header Background",
    type: "hex_color",
    templates: ["tournament-points-table"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Table Colors"
  },
  pointsTableHeaderText: {
    label: "Header Text",
    type: "hex_color",
    templates: ["tournament-points-table"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "Table Colors"
  },
  // --- Recent Tournaments List Settings ---
  showTournamentShortName: {
    label: "Show Tournament Short Name",
    type: "switch",
    templates: ["recent-tournaments-list"],
    default: true,
    isGlobal: false,
    group: "List Options" // New Group
  },
  recentTournamentsHeaderBg: {
    label: "Header Background",
    type: "hex_color",
    templates: ["recent-tournaments-list"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "List Colors" // New Group
  },
  recentTournamentsHeaderText: {
    label: "Header Text",
    type: "hex_color",
    templates: ["recent-tournaments-list"],
    // Default depends on dark mode, handled dynamically in getValue
    isGlobal: false,
    group: "List Colors" // New Group
  },
};




// --- Default Color Values based on Dark Mode ---
const defaultColors = {
  light: {
    bg_color: "#ffffff",
    text_color: "#1a202c",
    cardTextColor: "#1a202c",
    cardBackgroundColor: "#ffffff",
    liveColor: "#e53e3e",
    homeTeamColor: "#3b82f6",
    awayTeamColor: "#ef4444",
    pointsTableHeaderBg: "#f4f4f4",
    pointsTableHeaderText: "#4a5568",
    recentTournamentsHeaderBg: "#f4f4f4",
    recentTournamentsHeaderText: "#4a5568",
  },
  dark: {
    bg_color: "#1a202c",
    text_color: "#e2e8f0",
    cardTextColor: "#e2e8f0",
    cardBackgroundColor: "#2d3748", // Example dark card bg
    liveColor: "#f56565",
    homeTeamColor: "#4299e1",
    awayTeamColor: "#f56565",
    pointsTableHeaderBg: "#2d3748",
    pointsTableHeaderText: "#a0aec0",
    recentTournamentsHeaderBg: "#2d3748",
    recentTournamentsHeaderText: "#a0aec0",
  }
};


// Main Component
const TemplateSettings = ({ template, formDetails, setFormDetails }) => {

    // Helper to get current value, considering global vs template and dark mode defaults
    const getValue = (key, config) => {
        const isDark = formDetails.widget_data?.dark_mode === true;
        const modeDefaults = isDark ? defaultColors.dark : defaultColors.light;

        let defaultValue = config.default;
        // Override default for colors based on mode if not explicitly set in config
        if (config.type === 'hex_color' && modeDefaults[key]) {
            defaultValue = modeDefaults[key];
        }

        if (config.isGlobal) {
            return formDetails.widget_data?.[key] ?? defaultValue;
        } else {
            return formDetails.widget_data?.template_data?.[key] ?? defaultValue;
        }
    };


  // MODIFIED handleChange to save based on isGlobal flag
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const config = appearanceSettingsConfig[name];
    if (!config) return; // Should not happen

    const val = type === 'checkbox' || type === 'switch' ? checked : value;

    setFormDetails((prev) => {
        const newData = { ...prev.widget_data };

        if (config.isGlobal) {
            // Save directly into widget_data
            newData[name] = val;
        } else {
            // Save into template_data (ensure it exists)
            if (!newData.template_data) {
                newData.template_data = {};
            }
            newData.template_data[name] = val;
        }

        return {
            ...prev,
            widget_data: newData,
        };
    });
  };

  // MODIFIED handleSelectChange for Font Family (assumed not global)
  const handleSelectChange = (name, selectedOption) => {
     const config = appearanceSettingsConfig[name];
     if (!config) return;

     setFormDetails((prev) => {
        const newData = { ...prev.widget_data };
         if (!newData.template_data) {
             newData.template_data = {};
         }
         newData.template_data[name] = selectedOption ? selectedOption.value : null;

         return {
             ...prev,
             widget_data: newData,
         };
     });
  };

  // Generic field rendering function
  const renderField = (key, config) => {
    const currentValue = getValue(key, config); // Use helper to get value

    switch (config.type) {
      case "select":
        return (
          <Select
            id={key}
            name={key}
            options={config.options}
            // Ensure value matches an option object or is null
            value={config.options.find(opt => opt.value === currentValue) || null}
            onChange={(selectedOption) => handleSelectChange(key, selectedOption)}
          />
        );
      case "switch":
        return (
          <div className="d-flex">
            <Switch
              id={key}
              name={key}
              offColor="danger"
              onColor="success"
              offText="NO"
              onText="YES"
              value={currentValue === true}
              onChange={(el, state) => {
                const mockEvent = { target: { name: key, checked: state, type: 'switch' } };
                handleChange(mockEvent);
              }}
            />
          </div>
        );
      case "hex_color":
        return (
          <Input
            type="color"
            name={key}
            id={key}
            value={currentValue}
            onChange={handleChange}
            style={{ height: "38px", width: "100%", maxWidth: "100px" }}
            className="form-control-color"
          />
        );
      default:
        return null;
    }
  };


const relevantSettings = Object.entries(appearanceSettingsConfig).filter(
  ([key, config]) => {
    const appliesToTemplate = config.templates.includes(template);
    if (!appliesToTemplate) return false;

    const isGlobalThemeColor = key === 'bg_color' || key === 'text_color';

    if (isGlobalThemeColor) {
        const isDarkModeEnabled = formDetails.widget_data?.dark_mode === true;
        if (!isDarkModeEnabled) {
            return false; // Hide global colors if dark mode is OFF
        }

        const isTournamentCardTemplate = template === 'tournament' || template === 'single-match';
         if (isTournamentCardTemplate) {
            return false; // Hide global bg/text color for these templates even if dark mode is ON
         }
    }
    return true;
  }
);

  return (
    <div>
      <h5 className="fw-bold mb-3">Appearance Customization</h5>
      <FormText className="mb-3 d-block">
        Customize the global theme and the look and feel of your selected template.
      </FormText>

      {!template ? (
         <NoSettings message="Select a template to view its settings." />
      ) : relevantSettings.length === 0 ? (
         <NoSettings message="This template has no specific settings." />
      ) : (
        <Row>
          <Col>
            {relevantSettings.map(([key, config]) => (
              <FormGroup key={key} className="mt-3">
                  <Row className="align-items-center">
                      <Col md="4" className="mb-1 mb-md-0">
                          <Label htmlFor={key} className="text-md-end text-start fw-bold small mb-0">
                              {config.label}
                          </Label>
                      </Col>
                      <Col md="8">
                          {renderField(key, config)}
                      </Col>
                  </Row>
              </FormGroup>
            ))}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TemplateSettings;